import React, { useEffect, useState } from "react";
import { checkValidObject, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import env from "environment_system/env_system";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import {
    Row,
    Col,
    CardHeader,
    Card,
    Button,
    CardBody,
    CardFooter,
    Form,
    Input,
    Label,
} from "reactstrap";
import * as Yup from "yup";
import FormLoading from "Components/FormLoading";
import { RootState } from "index";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import ErrorTooltip from "Components/ErrorTooltip";
import { EventPrecedence } from "shared/constants";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import ErrorComponent from "Components/ErrorComponent";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface AnchorageDetailsComponentType {
    toggleTab: any;
    activeTab: any;
    activeAddTab: number;
    toggleAddTab: any;
    setErrorMessage: (value: boolean) => void,
    editReportBool: boolean,
}

const AnchorageDetailsComponent = ({
    toggleTab,
    activeTab,
    activeAddTab,
    toggleAddTab,
    setErrorMessage,
    editReportBool
}: AnchorageDetailsComponentType) => {
    /** State variables start */
    const { discardwarning } = useEditReport();
    const { VesselID, VoyageID, ReportID, CurrentReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const anchorageDetailsCurrentReport = CurrentReport?.data?.deck_officer?.lfonds_anchorage_details ?? null;
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const dispatch = useDispatch();
    const [refreshKey, setRefreshKey] = useState(0);
    const formikContext = useSafeFormikContext<any>();
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    /** State variables end */

    /** Assign values to initial object of Achorage Details */
    const getInitailAnchorageValues = () => {
        if (checkValidObject(editInitialValues?.deck_officer?.lfonds_anchorage_details)) {
            return editInitialValues?.deck_officer?.lfonds_anchorage_details;
        } else if (checkValidObject(anchorageDetailsCurrentReport)) {
            return anchorageDetailsCurrentReport;
        } else {
            return {
                anchored: null,
                anchorage_number: null,
                reason_for_anchoring: null,
                let_go_anchor: null,
                commence_heaving_anchor: null,
                anchor_aweigh: null,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            };
        }
    };

    /** Anchorage Detail's formik object */
    const AnchorageDetailsFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: getInitailAnchorageValues(),
        validationSchema: Yup.object().shape({
            anchorage_number: Yup.string()
                .matches(
                    /^[a-zA-Z0-9/.,-]{1,100}$/,
                    "Please enter up to 100 characters using only letters, numbers, hyphens (-), slashes (/), periods (.), or commas (,)."
                )
                .nullable(),
            reason_for_anchoring: Yup.string().max(250).nullable(),
        }),
        onSubmit: async (values: any, actions: any) => {
            if (env?.form_validation === false) {
                setErrorMessage(null)
                toggleAddTab(activeAddTab + 1);
            }
            actions.setSubmitting(true);
            let responseArray: any = [];
            if (checkValidObject(anchorageDetailsCurrentReport)) {
                responseArray.push(apiGlobal.put(`/lfonds_anchorage_details/${AnchorageDetailsFormik?.values.id}/`, values));
            } else {
                responseArray.push(apiGlobal.post(`/lfonds_anchorage_details/`, values));
            }
            await handleServerResponse(responseArray).then(async (res: any) => {
                if (res === true) {
                    dispatch(getCurrentReportAction(VesselID, ReportID));
                    setRefreshKey(refreshKey + 1);
                    if (env?.form_validation === true) {
                        setErrorMessage(null)
                        toggleAddTab(activeAddTab + 1);
                    }
                } else {
                    setErrorMessage(res)
                }
            })
            actions.setSubmitting(false);
        }
    });

    /** useEffect */
    useEffect(() => {
        if (checkValidObject(editInitialValues?.deck_officer?.lfonds_anchorage_details)) {
            setEditValues({
                ...editInitialValues,
                deck_officer: {
                    ...editInitialValues?.deck_officer,
                    lfonds_anchorage_details: AnchorageDetailsFormik?.values
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AnchorageDetailsFormik?.values])
    /** useEffect end */
    return (
        <Card className="p-0 mb-0">
            <CardHeader className="p-2">
                <div className="text-center mb-4">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">Anchorage Details</h4>
                            <p className="card-title-desc pos-start">
                                All readings since last report
                            </p>
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {reportingLoading && <FormLoading />}
            {reportingError && <ErrorComponent message="Error loading required data!" />}
            {!reportingLoading && !reportingError &&
                <Form autoComplete="off" onSubmit={AnchorageDetailsFormik?.handleSubmit} noValidate>
                    <CardBody className="px-2 py-0 mt-2">
                        <Row className="mb-3">
                            <Col lg={3}>
                                <Label className="mb-0" for="reason_for_anchoring">
                                    Reason for anchoring
                                </Label>
                                <Input
                                    onFocus={(e) => e.target.select()}
                                    name="reason_for_anchoring"
                                    autoFocus={true}
                                    as="textarea"
                                    className="form-control"
                                    id="reason_for_anchoring"
                                    rows={4}
                                    onBlur={(e: any) => AnchorageDetailsFormik?.handleChange(e)}
                                    defaultValue={AnchorageDetailsFormik?.values?.reason_for_anchoring}
                                />
                                {AnchorageDetailsFormik?.errors?.reason_for_anchoring &&
                                    env?.form_validation === true && !discardwarning && (
                                        <ErrorTooltip
                                            target="reason_for_anchoring"
                                            message={AnchorageDetailsFormik?.errors?.reason_for_anchoring}
                                            open={
                                                AnchorageDetailsFormik?.errors?.reason_for_anchoring &&
                                                    AnchorageDetailsFormik?.errors?.reason_for_anchoring
                                                    ? true
                                                    : false
                                            }
                                        />
                                    )}
                            </Col>
                            <Col lg={3}>
                                <Label className="mb-0" for="anchored">
                                    Anchored
                                </Label>
                                <Input
                                    onFocus={(e) => e.target.select()}
                                    name="anchored"
                                    type="time"
                                    className="form-control"
                                    id={"anchored"}
                                    onChange={(e: any) => AnchorageDetailsFormik?.handleChange(e)}
                                    defaultValue={AnchorageDetailsFormik?.values?.anchored}
                                />
                                {AnchorageDetailsFormik?.errors.anchored && env?.form_validation === true && !discardwarning && (
                                    <ErrorTooltip
                                        target={"anchored"}
                                        message={AnchorageDetailsFormik?.errors.anchored}
                                        open={AnchorageDetailsFormik?.errors.anchored ? true : false}
                                    />
                                )}
                            </Col>
                            <Col lg={3}>
                                <Label className="mb-0" for="anchorage_number">
                                    Anchorage number
                                </Label>
                                <Input
                                    onFocus={(e) => e.target.select()}
                                    name="anchorage_number"
                                    type="text"
                                    className="form-control"
                                    id="anchorage_number"
                                    onBlur={(e: any) => AnchorageDetailsFormik?.handleChange(e)}
                                    defaultValue={AnchorageDetailsFormik?.values?.anchorage_number}
                                />
                                {AnchorageDetailsFormik?.errors.anchorage_number &&
                                    env?.form_validation === true && !discardwarning && (
                                        <ErrorTooltip
                                            target={"anchorage_number"}
                                            message={AnchorageDetailsFormik?.errors.anchorage_number}
                                            open={AnchorageDetailsFormik?.errors.anchorage_number ? true : false}
                                        />
                                    )}
                            </Col>
                            <Col lg={3}>
                                <Label className="mb-0" for="let_go_anchor">
                                    Let go anchor
                                </Label>
                                <Input
                                    onFocus={(e) => e.target.select()}
                                    name="let_go_anchor"
                                    type="time"
                                    className="form-control"
                                    id={"let_go_anchor"}
                                    onChange={(e: any) => AnchorageDetailsFormik?.handleChange(e)}
                                    defaultValue={AnchorageDetailsFormik?.values?.let_go_anchor}
                                />
                                {AnchorageDetailsFormik?.errors.let_go_anchor && env?.form_validation === true && !discardwarning && (
                                    <ErrorTooltip
                                        target={"let_go_anchor"}
                                        message={AnchorageDetailsFormik?.errors.let_go_anchor}
                                        open={AnchorageDetailsFormik?.errors.let_go_anchor ? true : false}
                                    />
                                )}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col lg={3}>
                                <Label className="mb-0" for="commence_heaving_anchor">
                                    Commence heaving anchor
                                </Label>
                                <Input
                                    onFocus={(e) => e.target.select()}
                                    name="commence_heaving_anchor"
                                    type="time"
                                    className="form-control"
                                    id={"commence_heaving_anchor"}
                                    onChange={(e: any) => AnchorageDetailsFormik?.handleChange(e)}
                                    disabled={
                                        checkValidObject(currentVesselReport) &&
                                        currentVesselReport?.event_precedence_id === EventPrecedence.DROP_ANCHOR
                                    }
                                    defaultValue={AnchorageDetailsFormik?.values?.commence_heaving_anchor}
                                />
                                {AnchorageDetailsFormik?.errors.commence_heaving_anchor && !discardwarning &&
                                    env?.form_validation === true && (
                                        <ErrorTooltip
                                            target={"commence_heaving_anchor"}
                                            message={AnchorageDetailsFormik?.errors.commence_heaving_anchor}
                                            open={AnchorageDetailsFormik?.errors.commence_heaving_anchor ? true : false}
                                        />
                                    )}
                            </Col>
                            <Col lg={3}>
                                <Label className="mb-0" for="anchor_aweigh">
                                    Anchor aweigh
                                </Label>
                                <Input
                                    onFocus={(e) => e.target.select()}
                                    name="anchor_aweigh"
                                    type="time"
                                    className="form-control"
                                    id={"anchor_aweigh"}
                                    onChange={(e: any) => AnchorageDetailsFormik?.handleChange(e)}
                                    disabled={
                                        checkValidObject(currentVesselReport) &&
                                        currentVesselReport?.event_precedence_id === EventPrecedence.DROP_ANCHOR
                                    }
                                    defaultValue={AnchorageDetailsFormik?.values?.anchor_aweigh}
                                />
                                {AnchorageDetailsFormik?.errors.anchor_aweigh && env?.form_validation === true && !discardwarning && (
                                    <ErrorTooltip
                                        target={"anchor_aweigh"}
                                        message={AnchorageDetailsFormik?.errors.anchor_aweigh}
                                        open={AnchorageDetailsFormik?.errors.anchor_aweigh ? true : false}
                                    />
                                )}
                            </Col>
                        </Row>
                    </CardBody>
                    <CardFooter className="p-2 py-3">
                        <Row className="d-flex align-items-center flex-row-reverse">
                            <Col lg={10}>
                                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                                    {/* {editReportBool && (
                                        <Button
                                            type="reset"
                                            color="danger"
                                            className="btn_size_cstm"
                                            onClick={() => {
                                                setEditValues({
                                                    ...editValues,
                                                    deck_officer: {
                                                        ...editValues?.deck_officer,
                                                        lfonds_anchorage_detail: editInitialValues?.deck_officer?.lfonds_anchorage_detail,
                                                    }
                                                });
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    )} */}
                                    <Button
                                        type={editReportBool ? "button" : "submit"}
                                        color="primary"
                                        className="btn_size_cstm"
                                        disabled={AnchorageDetailsFormik?.isSubmitting || editIsSubmitting}
                                        onClick={async () => {
                                            const validationErrors = await AnchorageDetailsFormik?.validateForm();
                                            Object.keys(AnchorageDetailsFormik?.values || {}).forEach((key: any) => {
                                                AnchorageDetailsFormik?.setFieldTouched(key, true);
                                            });
                                            if (editReportBool && Object.keys(validationErrors).length === 0) {
                                                await saveEditForm(editSubmit, toggleAddTab, activeAddTab);
                                            }
                                        }}
                                    >
                                        {(AnchorageDetailsFormik?.isSubmitting || editIsSubmitting) && (
                                            <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                        )}
                                        Next <i className="bx bx-chevron-right ms-1" />
                                    </Button>
                                </div>
                            </Col>
                            <Col lg={2}>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm"
                                    onClick={() => {
                                        AnchorageDetailsFormik?.setErrors({});
                                        setErrorMessage(null);
                                        toggleTab(activeTab - 1);
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </Col>
                        </Row>
                    </CardFooter>
                    <FormValuesDebug
                        values={[AnchorageDetailsFormik?.values, AnchorageDetailsFormik?.errors]}
                    />
                </Form>
            }
        </Card>
    );
};

export default AnchorageDetailsComponent;